import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import {
  DataService as CoreDataService,
  PersistenceService,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
} from '@barthauer/abc/core/data';

import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbcPopupComponent,
  AbcSpeedDialActionComponent,
  _AbcContextMenuComponent,
  _AbcFileUploaderComponent,
  _AbcFormComponent,
  _AbcGridComponent,
  _AbcSelectBoxComponent,
} from '@barthauer/abc/ui-components';
import { AddressPerson, DefaultIcons, DocumentKind, GeoDsDocument, Person } from '@barthauer/custom/xmt-models';
import { DxDataGridModule, DxFileUploaderModule, DxFormModule, DxSelectBoxComponent } from 'devextreme-angular';
import { loadMessages, locale } from 'devextreme/localization';
import deMessages from 'devextreme/localization/messages/de.json';
import enMessages from 'devextreme/localization/messages/en.json';
import { Column } from 'devextreme/ui/data_grid';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {  NotificationMessage, PermessionType,  ToastType } from '../../models/enumeration-list.model';
import { AppService } from '../../services/app.service';
import { DataService, ObjectKeys } from '../../services/data.service';
import { LanguageService } from '../../services/language.service';
import { NotificationService } from '../../services/notification.service';
import { SharedModule } from '../../shared/shared.module';
import { StaffMembersService } from './staff-members.service';
import { CheckPermissionDirective } from '../../directives/check-permession-directive';
@Component({
  selector: 'ubt-geods-staff-member',
  templateUrl: './staff-members.component.html',
  styleUrls: ['./staff-members.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DxFileUploaderModule,
    DxDataGridModule,
    SharedModule,
    DxFormModule,
    _AbcFormComponent,
    _AbcGridComponent,
    AbcPopupComponent,
    AbcSpeedDialActionComponent,
    _AbcContextMenuComponent,
    _AbcSelectBoxComponent,
    CheckPermissionDirective
  ],
})
export class StaffMembersComponent implements OnInit, OnDestroy {
  @ViewChild('staffMembersGrid') staffMembersGrid: _AbcGridComponent;
  @ViewChild('fileUpload') staffMembersUploader: _AbcFileUploaderComponent;
  @ViewChild('staffMemberPopup') staffMemberPopup: AbcPopupComponent;
  @ViewChild('confirmDeletePopup') confirmDeletePopup: AbcPopupComponent;
  @ViewChild('contextMenu') contextMenu: _AbcContextMenuComponent;
  @ViewChild('projectRoleSelectBox', { static: true }) projectRoleSelectBox: DxSelectBoxComponent;
  @ViewChild('form') form: _AbcFormComponent;
  public data$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  public uploadDocument: GeoDsDocument = new GeoDsDocument(null);
  public staffMember: Person = new Person(null);
  public staffMemberPosition: AddressPerson = new AddressPerson(null);
  public documents: GeoDsDocument[] = [];
  public staffMembers: Person[] = [];
  public uploaded = false;
  public availableRoles: IRole[] = [];
  public availablePosition: IRole[] = [];
  public personColumns: QueryColumn[] = [];
  public functionColumns: QueryColumn[] = [];
  public documentsColumns: QueryColumn[] = [];
  public columnsHeader: Column[] = [
    {
      caption: '',
      dataField: 'ProfilePicture',
      dataType: 'string',
      visibleIndex: 0,
    },
    {
      caption: this.dataService.res('Ubt-CreateStaffMembers-FirstName'),
      dataField: 'FirstName',
      dataType: 'string',
      visibleIndex: 0,
    },
    {
      caption: this.dataService.res('Ubt-CreateStaffMembers-LastName'),
      dataField: 'LastName',
      dataType: 'string',
      visibleIndex: 0,
    },
    {
      caption: this.dataService.res('Ubt-CreateStaffMembers-EMail'),
      dataField: 'EMail',
      dataType: 'string',
      visibleIndex: 0,
    },
    {
      caption: this.dataService.res('Ubt-StaffMembers-Project-Role'),
      dataField: 'AddressPerson[0].Position',
      dataType: 'string',
      visibleIndex: 0,
    },
    {
      caption: this.dataService.res('Ubt-CreateStaffMembers-Function'),
      dataField: 'Function',
      dataType: 'string',
      visibleIndex: 0,
      visible: false,
    },
    {
      type: 'buttons',
      caption: '',
      alignment: 'left',
      dataField: '',
      visibleIndex: 6,
      minWidth: 50,
      width: '50px',
    },
  ];
  public Action=PermessionType
  public contextMenuItems: any[] = [
    {
      text: this.dataService.res('Ubt-StaffMember-Edit-Staff-Member'),
      onClick: () => {
        this.openStaffMemberDialog(true);
      },
    },
    {
      text: this.dataService.res('Ubt-StaffMember-Delete-Staff-Member'),
      onClick: () => {
        this.openConfirmDialog();
      },
    },
  ];
  public submitText: string;
  public cancelButtonOptions = {
    text: this.dataService.res('Ubt-CreateMeasure-Cancel'),
    onClick: () => this.clearWindow(),
  };
  private subscriptions: Subscription[] = [];
  private isUpdating = false;
  private staffMemberId: string;
  private shouldReload = true;
  public noDataText = this.dataService.res('Ubt-No-Data-text');
  public readonly exportFileName =
    this.dataService.res('Ubt-TreeList-StaffMembers') + '_' + this.datePipe.transform(new Date(), 'dd.MM.yyyy');

  constructor(
    public dataService: DataService,
    public appService: AppService,
    private persistenceService: PersistenceService,
    private coreDataService: CoreDataService,
    private datePipe: DatePipe,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private staffMembersService: StaffMembersService,
    private languageService: LanguageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  public ngOnInit(): void {
    this.dataService.floatingButtonConfig();
    this.appService.showLoader(true);
    this.languageService.localStorageChanges$.subscribe((value) => {
      this.updatedTranslation(value);
    });
    const queries = forkJoin([
      this.dataService.loadListDataSource(ObjectKeys.PERSON, 'Function'),
      this.coreDataService.executeReadObjectsQuery(this.staffMembersService.getPersonWithImages()),
      this.dataService.loadListDataSource(ObjectKeys.ADDRESSPERSON, 'Position'),
    ]);
    queries.subscribe(
      (data: any) => {
        this.availableRoles = data[0].Data;
        this.availablePosition = data[2].Data;
        if (Array.isArray(data[1]) && data[1].length > 0) {
          this.staffMembers = data[1];
          const updatedData = this.staffMembers;
          this.data$.next(updatedData);
        } else {
          this.notificationService.callNotification({ message: NotificationMessage.Empty, type: ToastType.INFO });
        }
      },
      (error) => {
        this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
        this.appService.showLoader(false);
      },
      () => {
        this.staffMembers.sort((a, b) => a?.FirstName.localeCompare(b?.FirstName));
        this.appService.showLoader(false);
      }
    );
    this.dataService.uploadData$.subscribe((uploadData: GeoDsDocument) => {
      this.uploadDocument = uploadData;
      this.uploaded = true;
    });
    this.subscriptions.push(
      this.dataService.updateGridData$.subscribe(() => {
        this.getStaffMemberById(this.staffMemberId);
      })
    );
  }

  updatedTranslation(value: String) {
    if (value === 'de' || value == null) {
      locale('de');
      loadMessages(deMessages);
    } else {
      loadMessages(enMessages);
      locale('en');
    }
  }

  public onCellPrepared = (e) => {
    if (e.rowType === 'data') {
      e.cellElement.style.verticalAlign = 'middle';

      if (e.column.dataField === 'ProfilePicture') {
        const img = document.createElement('IMG');
        e.cellElement.style.textAlign = 'center';

        if (e.data?.Documents) {
          const object = e.data.Documents[0].Object;
          img.setAttribute('src', 'data:image/png;base64,' + object);
          this.cdr.markForCheck();
        } else {
          e.cellElement.innerHTML = '';
          img.setAttribute('src', 'assets/images/Avatar.svg');
          this.cdr.markForCheck();
        }
        img.style.borderRadius = '50%';
        img.style.width = '30%';
        e.cellElement.appendChild(img);
      } else if (e.column.dataField === '') {
        e.cellElement.style.cursor = 'pointer';
        return (e.cellElement.innerHTML = DefaultIcons.CONTEXTMENU);
      }
    }
  };

  public onCellClick = (e) => {
    this.staffMemberId = e.data.Id;
    this.staffMember = e.data;
    if (e.columnIndex === 5) {
      e.cellElement.className = 'context-menu-icon' + e.rowIndex;
      this.contextMenu.target = '.context-menu-icon' + e.rowIndex;
      e.cellElement.innerHTML = this.contextMenu.visible ? DefaultIcons.CLOSE : DefaultIcons.CONTEXTMENU;
      this.contextMenuItems[0].visible=this.dataService.hasPermessionForSpecificModule('Stuff-Members',PermessionType.UPDATE)
      this.contextMenuItems[1].visible=this.dataService.hasPermessionForSpecificModule('Stuff-Members',PermessionType.DELETE)
      this.contextMenu.instance.show();
    }
  };
  public getDocumentQuery(oPath?: string): QueryObjectsModel {
    this.personColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('FirstName', 'FirstName', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('LastName', 'LastName', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('EMail', 'EMail', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Login', 'Login', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Function', 'Function', QueryColumnSortOrder.None),
    ];
    const documentsColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Designation', 'Designation', QueryColumnSortOrder.None),
      {
        Name: 'Object',
        OPath: 'Object',
        Format: 'Base64Image[80x80]',
      },
    ];
    const positionColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Position', 'Position'),
    ];
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = ObjectKeys.PERSON;
    mainQuery.Columns = this.personColumns;
    mainQuery.OPath = 'ParentId=' + "'" + localStorage.getItem('currentCompany') + "'" + " && Not(LastName = 'Admin')";

    if (oPath) mainQuery.OPath = oPath;
    const subQuery: Query = new Query();
    subQuery.Name = 'Documents';
    subQuery.OPath = 'Documents';
    subQuery.Columns = documentsColumns;
    const positionQuery: Query = new Query();
    positionQuery.Name = 'AddressPerson';
    positionQuery.OPath = 'AddressPerson_IdRefPerson_Person';
    positionQuery.Columns = positionColumns;
    mainQuery.ObjectQueries = [subQuery, positionQuery];
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];
    return queryDocument;
  }

  public openStaffMemberDialog(isEditing: boolean): void {
    this.staffMemberPosition = new AddressPerson(null);
    this.staffMemberPosition.Position = this.availablePosition[2].Value;
    if (isEditing === true) {
      this.staffMemberPopup.title = this.dataService.res('Ubt-StaffMembers-Update-StaffMembers');
      this.submitText = 'Update';
      if (this.staffMember['AddressPerson']) {
        const addressPerson = new AddressPerson(this.staffMember['AddressPerson'][0]);
        this.staffMemberPosition.Id = addressPerson.Id;
        this.staffMemberPosition.Position = addressPerson.Position;
      }
    } else {
      this.uploadDocument = new GeoDsDocument(null);
      this.staffMember = new Person(null);
      this.form.instance.resetValues();
      this.staffMemberPopup.title = this.dataService.res('Ubt-StaffMembers-Create-StaffMembers');
      this.submitText = 'Create';
    }

    this.staffMemberPopup.instance.show();
  }

  public persistStaffMember(event, staffMember) {
    event.preventDefault();
    this.subscriptions.push(
      this.staffMembersService
        .persistStaffMember(staffMember)
        .pipe(
          switchMap((data) => {
            this.staffMemberId = data.Id;
            const addressId = localStorage.getItem('currentCompany');
            this.staffMemberPosition.IdRefPerson = this.staffMemberId;
            this.staffMemberPosition.ParentId = addressId;
            const index = this.staffMembers.findIndex((member) => member.Id === this.staffMember.Id);
            this.staffMembers[index] = this.staffMember;
            const document = this.getDocument(
              data.Id,
              this.staffMember.Documents ? this.staffMember.Documents[0]['Id'] : null
            );
            return forkJoin([
              this.createDocument(document),
              this.staffMemberPosition.Position ? this.createPosition(this.staffMemberPosition) : of(null),
            ]);
          })
        )
        .subscribe(
          (data) => {},
          (error) => {
            this.appService.showLoader(false);
          },
          () => {
            this.clearWindow();
            this.cdr.detectChanges();
          }
        )
    );
  }

  private createDocument = (document): Observable<any> => {
    if (this.uploadDocument.Object && this.uploadDocument.FileName?.length > 0) {
      return of(this.dataService.persistUploadDocument(document));
    } else {
      return of([this.getStaffMemberById(this.staffMemberId)]);
    }
  };

  private createPosition = (memberPosition: AddressPerson): Observable<any> => {
    return of(this.persistStafMemeberPosition(memberPosition));
  };

  public persistStafMemeberPosition = (memberPosition: AddressPerson): void => {
    this.staffMembersService.persistStafMemeberPosition(memberPosition).subscribe(
      (_position) => {
        this.staffMemberPosition.Id = _position.Id;
      },
      (error) => {
        this.notificationService.callNotification({ message: error, type: ToastType.ERROR });
        this.appService.showLoader(false);
      },
      () => {}
    );
  };

  public getDocument(parentId: string, documentId?: string): GeoDsDocument {
    this.uploadDocument.Id = documentId;
    this.uploadDocument.ParentId = parentId;
    this.uploadDocument.Designation = 'imgStaffMember';
    this.uploadDocument.ParentObjectType = ObjectKeys.PERSON;
    this.uploadDocument.Kind = DocumentKind.Bild;
    return this.uploadDocument;
  }

  private getStaffMemberById(id: string): void {
    const opath = 'Id=' + "'" + this.staffMemberId + "'";
    this.subscriptions.push(
      this.coreDataService.executeReadObjectsQuery(this.staffMembersService.getPersonWithImages(opath)).subscribe(
        (data) => {
          if (this.staffMembersService.isUpdating === true) {
            let selectedIndex: number;
            this.staffMembers.forEach((item, index) => {
              if (item.Id.toString() == id) {
                selectedIndex = index;
              }
            });
            this.staffMembers[selectedIndex] = new Person(data[0]);
            this.staffMembersGrid.instance.refresh();
          } else {
            this.staffMembers.push(new Person(data[0]));
          }
          this.notificationService.callNotification({
            message: NotificationMessage.ValidUpdateMessage,
            type: ToastType.SUCCESS,
          });
        },
        (error) => {
          this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
        }
      )
    );
  }

  public clearWindow(): void {
    this.staffMemberPopup.instance.hide();
    this.uploadDocument = new GeoDsDocument(null);
    this.staffMembersUploader.instance.reset();
    this.appService.showLoader(false);
    this.cdr.detectChanges();
  }

  public onFileUpload(e, document): void {
    this.dataService.onFileChanged(e, document);
  }

  public removeStaffMember(): void {
    this.subscriptions.push(
      this.dataService.deleteObject(ObjectKeys.PERSON, this.staffMemberId).subscribe(
        () => {
          this.confirmDeletePopup.instance.hide();
          const index = this.staffMembers.findIndex((item) => item.Id === this.staffMemberId);
          this.staffMembers.splice(index, 1);
          this.notificationService.callNotification({
            message: NotificationMessage.ValidDeleteMessage,
            type: ToastType.SUCCESS,
          });
        },
        (error) => {
          this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
        }
      )
    );
  }

  public openConfirmDialog(): void {
    this.confirmDeletePopup.instance.show();
  }

  public abortDelete(): void {
    this.confirmDeletePopup.instance.hide();
  }

  public ngOnDestroy(): void {
    this.subscriptions.map((subscription) => {
      subscription.unsubscribe();
    });
  }
}

interface IRole {
  Description: string;
  Value: string;
}
